import { useRouteSummary } from '$contexts/RouteContext/hooks'
import { useAppUserService } from '$hooks/services'
import { redirect, ROUTE_NAMES } from '$router/config'
import { useMount, useUnmount } from 'ahooks'
import Loading from '$components/Loading'
import { ChevronLeft } from 'lucide-react'
import { Button } from '@genie-fintech/ui/components'
import { useCallback, useRef } from 'react'
import Header from '$layouts/LayoutWithSidebar/Header'
import { title } from '@genie-fintech/ui/style/typography'
import { ButtonVariants } from '@genie-fintech/ui/style/element'
import HeaderDropdownAction from './HeaderDropdownAction'
import UserInfo from './UserInfo'
import { useBreadcrumb } from '$components/Breadcrumb/hooks/useBreadcrumb'
import { useAssignRoleStore } from '$hooks/stores'
import { TRoles } from '$services/types'
import AppUserStatusSetting from './AppUserStatusSetting'

const buttonVariants: ButtonVariants = {
  kind: 'neutral',
  type: 'outlined',
  size: 'small'
}

const AppUserDetail = () => {
  const { route } = useRouteSummary()

  const terminateButtonRef = useRef<HTMLButtonElement>(null)
  const addUserRoles = useAssignRoleStore(s => s.setUserRoles)
  const deleteUserRole = useAssignRoleStore(s => s.deleteUserRole)
  const resetRoles = useAssignRoleStore(s => s.resetUserRoles)

  const { appId, userId } = route.params

  const {
    user,
    fetchAppUserDetailAsync,
    fetchingAppUserDetail,
    deleteAssignRole,
    deletingAssignRole
  } = useAppUserService()

  const onFetchUserDetail = useCallback(() => {
    if (!appId || !userId) return
    fetchAppUserDetailAsync({
      application_id: appId,
      user_id: userId
    }).then(res => addUserRoles(res.data.roles))
  }, [appId, userId, fetchAppUserDetailAsync, addUserRoles])

  useMount(onFetchUserDetail)

  useBreadcrumb([
    { name: 'Users', path: ROUTE_NAMES.APP_USERS },
    { name: user?.name ?? '' }
  ])

  const onBack = useCallback(() => {
    redirect(ROUTE_NAMES.APP_USERS, options => options)
  }, [])

  const handleOnDelete = useCallback(
    (id: number, deleteRole: TRoles) => {
      if (!appId || !userId) return
      deleteAssignRole({ appId, userId, roleId: id }).then(() =>
        deleteUserRole(deleteRole)
      )
    },
    [appId, deleteAssignRole, deleteUserRole, userId]
  )

  useUnmount(resetRoles)

  const initialLoading = fetchingAppUserDetail && !user

  if (initialLoading || !user) return <Loading />

  return (
    <>
      <Header style={{ gap: 8 }}>
        <Button styleVariants={buttonVariants} onClick={onBack}>
          <ChevronLeft size={16} />
          Back to listing
        </Button>

        <span className={title.two} style={{ paddingLeft: 4 }}>
          {user.name}
        </span>

        <span style={{ flex: 1 }} />

        <HeaderDropdownAction
          onUpdateUserStatus={() => terminateButtonRef.current?.click()}
          status={user.status}
        />
      </Header>

      <UserInfo
        user={user}
        appId={appId}
        handleOnDelete={handleOnDelete}
        isLoading={deletingAssignRole}
      />

      <AppUserStatusSetting
        buttonRef={terminateButtonRef}
        status={user.status}
        name={user.name}
        onSuccess={onFetchUserDetail}
      />
    </>
  )
}

export default AppUserDetail
